import React, { useCallback, useEffect, useState } from "react";
import { T } from "../theme/tokens.js";
import { Panel, Btn, Notice, Labelled, Field, Empty } from "./ui/index.jsx";
import { supabase, friendlyError } from "../lib/supabase.js";
import { fmtDate } from "../lib/format.js";

/* The company holiday calendar, maintained.
 *
 * Firm-wide and admin-only, because the eleven days apply to everyone and a
 * holiday is not a per-contract fact. It is the denominator of honest
 * utilization: a week with a holiday has fewer billable hours, so a person
 * full on that week reads at a hundred percent of a short week, not under on a
 * flat forty. Here an admin sees the calendar and adds or removes a day.
 */
export default function CompanyHolidays({ isAdmin }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ holiday_date: "", name: "", hours: 8 });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data, error: e } = await supabase
      .from("company_holidays").select("*").order("holiday_date");
    if (e) setError(friendlyError(e));
    else { setRows(data ?? []); setError(""); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!draft.holiday_date || !draft.name.trim()) return;
    setBusy(true); setError("");
    const { error: e } = await supabase.from("company_holidays").insert({
      holiday_date: draft.holiday_date, name: draft.name.trim(),
      hours: Number(draft.hours) || 8,
    });
    setBusy(false);
    if (e) setError(friendlyError(e));
    else { setAdding(false); setDraft({ holiday_date: "", name: "", hours: 8 }); await load(); }
  };

  const remove = async (id) => {
    const { error: e } = await supabase.from("company_holidays").delete().eq("id", id);
    if (e) setError(friendlyError(e)); else await load();
  };

  const upcoming = (rows ?? []).filter((r) => r.holiday_date >= new Date().toISOString().slice(0, 10)).length;

  return (
    <Panel title="Company holidays"
      right={isAdmin ? <Btn small kind="gold" onClick={() => setAdding((a) => !a)}>{adding ? "Cancel" : "Add a holiday"}</Btn> : null}>
      {error && <Notice kind="error">{error}</Notice>}
      <div style={{ fontSize: 12.5, color: T.soft, marginBottom: 12, lineHeight: 1.6 }}>
        The firm-wide calendar that sets how many hours are billable each week. Every holiday
        here lowers available time for everyone, so utilization reads against a real week, not
        a flat forty. The floating holiday each person may take is personal and lives on the worker, not here.
      </div>

      {adding && (
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 2fr 80px auto",
          alignItems: "end", marginBottom: 14, padding: 12, background: T.bg,
          border: `1px solid ${T.line}`, borderRadius: 6 }}>
          <Labelled label="Date">
            <Field f={{ type: "date" }} value={draft.holiday_date}
              onChange={(v) => setDraft({ ...draft, holiday_date: v })} />
          </Labelled>
          <Labelled label="Name">
            <Field f={{ type: "text" }} value={draft.name} placeholder="Juneteenth"
              onChange={(v) => setDraft({ ...draft, name: v })} />
          </Labelled>
          <Labelled label="Hours">
            <Field f={{ type: "number" }} value={draft.hours}
              onChange={(v) => setDraft({ ...draft, hours: v })} />
          </Labelled>
          <Btn kind="gold" onClick={add} disabled={busy || !draft.holiday_date || !draft.name.trim()}>
            {busy ? "Adding..." : "Add"}
          </Btn>
        </div>
      )}

      {rows === null ? <Empty text="Loading..." />
        : rows.length === 0 ? <Empty text="No holidays set. Add the company calendar to make utilization honest." />
        : (
          <>
            <div style={{ fontSize: 11.5, color: T.soft, marginBottom: 8 }}>
              {rows.length} holiday{rows.length === 1 ? "" : "s"}, {upcoming} still ahead this year.
            </div>
            <div style={{ display: "grid", gap: 1, background: T.line,
              border: `1px solid ${T.line}`, borderRadius: 6, overflow: "hidden" }}>
              {rows.map((r) => (
                <div key={r.id} style={{ display: "grid",
                  gridTemplateColumns: "120px 1fr 60px auto", gap: 10, alignItems: "center",
                  background: T.panel, padding: "8px 12px" }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.navy,
                    fontVariantNumeric: "tabular-nums" }}>{fmtDate(r.holiday_date)}</span>
                  <span style={{ fontSize: 13, color: T.ink }}>{r.name}</span>
                  <span style={{ fontSize: 12, color: T.soft }}>{Number(r.hours)}h</span>
                  {isAdmin
                    ? <Btn small kind="ghost" onClick={() => remove(r.id)}>Remove</Btn>
                    : <span />}
                </div>
              ))}
            </div>
          </>
        )}
    </Panel>
  );
}
